
import axios from 'axios';
import { GoogleGenerativeAI } from '@google/generative-ai';

const apiClient = axios.create({
  baseURL: '/api/v1',
  headers: {
    'x-api-version': '1.0.0',
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const login = (msisdn, password) => {
  return apiClient.post('/auth/login', { msisdn, password });
};

export const getFarmers = () => {
  return apiClient.get('/farmers/list');
};

export const getLivestock = (farmerId) => {
  return apiClient.get(`/farmers/${farmerId}/livestock`);
};

export const getLivestockServices = (farmerId, livestockId) => {
  return apiClient.get(`/farmers/${farmerId}/livestock/${livestockId}/services`);
};

const fileToGenerativePart = async (file) => {
  const base64EncodedData = await new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(',')[1]);
    reader.readAsDataURL(file);
  });
  return {
    inlineData: { data: base64EncodedData, mimeType: file.type },
  };
};

export const getAiSuggestion = async (livestockDetails, symptoms, image, livestockServices) => {
  const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY);
  const model = genAI.getGenerativeModel({
    model: 'gemini-1.5-flash',
    systemInstruction: 'You are an AI-powered veterinary assistant. Your goal is to suggest potential diseases and appropriate medications/treatments based on provided animal details and symptoms. Respond with a JSON object.',
  });

  const history = livestockServices && livestockServices.length > 0
    ? livestockServices.map(s => `${s.service_name} on ${new Date(s.visit_date).toLocaleDateString()}`).join(', ')
    : 'N/A';

  const prompt = `
    Animal Details:
    - Type: ${livestockDetails.livestock_type_name || 'N/A'}
    - Gender: ${livestockDetails.gender || 'N/A'}
    - Age: ${livestockDetails.age || 'N/A'}
    - Description: ${livestockDetails.breed ? `${livestockDetails.breed} breed, ` : ''} ${livestockDetails.description || 'N/A'}.
    - Previous History: ${history}
    ---
    Observed Symptoms/Diagnosis:
    - ${symptoms}
    ---
    Based on this information, provide a response in JSON format with two keys: "summary" and "detailed".
    - "summary": A concise list of likely diseases and recommended medications.
    - "detailed": A full breakdown including reasoning, dosages, administration routes, supportive care, and optional diagnostic steps or follow-up questions.
  `;

  const contents = [prompt];
  if (image) {
    const imagePart = await fileToGenerativePart(image);
    contents.push(imagePart);
  }

  const result = await model.generateContent(contents);
  const response = await result.response;
  const text = response.text();

  try {
    // Clean the text to ensure it's valid JSON
    const cleanText = text.replace(/```json|```/g, '').trim();
    return JSON.parse(cleanText);
  } catch (error) {
    console.error('Error parsing AI suggestion JSON:', error);
    // Return a fallback object or the raw text if parsing fails
    return { summary: 'Could not parse suggestion.', detailed: text };
  }
};
